export type FileFormat =
  | 'PDF' | 'DOCX' | 'DOC' | 'PPTX' | 'PPT' | 'RTF'
  | 'XLSX' | 'XLS' | 'CSV'
  | 'JPG' | 'PNG' | 'WEBP' | 'AVIF' | 'GIF' | 'SVG' | 'HEIC' | 'HEIF' | 'BMP' | 'TIFF'
  | 'TXT' | 'MD'
  | 'MP3' | 'MP4' | 'M4A' | 'WAV' | 'WEBM' | 'OGG' | 'FLAC' | 'AAC' | 'MOV'
  | 'ZIP' | 'JSON' | 'HTML' | 'XML'
  | 'UNKNOWN'

export type FormatCategory = 'document' | 'image' | 'spreadsheet' | 'presentation' | 'vector' | 'text' | 'audio' | 'unknown'

export interface FileFormatInfo {
  format: FileFormat
  category: FormatCategory
  label: string
  color: string
  textColor: string
  canSign: boolean
  needsConversionToSign: boolean
  canOCR: boolean
  isImage: boolean
  isPDF: boolean
  isAudio: boolean
}

/**
 * Format chip palette — every extension gets its own badge color so lists stay scannable.
 * Families: PDF red · Word blue · RTF indigo · Slides rose · Excel green · CSV teal · Images greens/cyans/limes (per format) · SVG sky · TXT slate · MD violet · Audio warm ambers (per codec) · Video purples (per container) · Archive/code neutrals.
 */
const FORMAT_MAP: Record<string, FileFormatInfo> = {
  pdf: {
    format: 'PDF', category: 'document', label: 'PDF',
    color: '#FEE2E2', textColor: '#991B1B',
    canSign: true, needsConversionToSign: false,
    canOCR: true, isImage: false, isPDF: true, isAudio: false,
  },
  docx: {
    format: 'DOCX', category: 'document', label: 'Word',
    color: '#DBEAFE', textColor: '#1D4ED8',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  doc: {
    format: 'DOC', category: 'document', label: 'Word',
    color: '#DBEAFE', textColor: '#1D4ED8',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  rtf: {
    format: 'RTF', category: 'document', label: 'RTF',
    color: '#E0E7FF', textColor: '#4338CA',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  pptx: {
    format: 'PPTX', category: 'presentation', label: 'PowerPoint',
    color: '#FCE7F3', textColor: '#9D174D',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  ppt: {
    format: 'PPT', category: 'presentation', label: 'PowerPoint',
    color: '#FCE7F3', textColor: '#9D174D',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  xlsx: {
    format: 'XLSX', category: 'spreadsheet', label: 'Excel',
    color: '#DCFCE7', textColor: '#166534',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  xls: {
    format: 'XLS', category: 'spreadsheet', label: 'Excel',
    color: '#DCFCE7', textColor: '#166534',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  csv: {
    format: 'CSV', category: 'spreadsheet', label: 'CSV',
    color: '#CCFBF1', textColor: '#0F766E',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  jpg: {
    format: 'JPG', category: 'image', label: 'JPG',
    color: '#D1FAE5', textColor: '#047857',
    canSign: false, needsConversionToSign: true,
    canOCR: true, isImage: true, isPDF: false, isAudio: false,
  },
  jpeg: {
    format: 'JPG', category: 'image', label: 'JPG',
    color: '#D1FAE5', textColor: '#047857',
    canSign: false, needsConversionToSign: true,
    canOCR: true, isImage: true, isPDF: false, isAudio: false,
  },
  png: {
    format: 'PNG', category: 'image', label: 'PNG',
    color: '#A7F3D0', textColor: '#065F46',
    canSign: false, needsConversionToSign: true,
    canOCR: true, isImage: true, isPDF: false, isAudio: false,
  },
  webp: {
    format: 'WEBP', category: 'image', label: 'WEBP',
    color: '#CFFAFE', textColor: '#0E7490',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  avif: {
    format: 'AVIF', category: 'image', label: 'AVIF',
    color: '#99F6E4', textColor: '#0F766E',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  gif: {
    format: 'GIF', category: 'image', label: 'GIF',
    color: '#ECFCCB', textColor: '#3F6212',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  heic: {
    format: 'HEIC', category: 'image', label: 'HEIC',
    color: '#6EE7B7', textColor: '#047857',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  heif: {
    format: 'HEIF', category: 'image', label: 'HEIF',
    color: '#6EE7B7', textColor: '#047857',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  bmp: {
    format: 'BMP', category: 'image', label: 'BMP',
    color: '#BEF264', textColor: '#3F6212',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  tif: {
    format: 'TIFF', category: 'image', label: 'TIFF',
    color: '#ECFCCB', textColor: '#3F6212',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  tiff: {
    format: 'TIFF', category: 'image', label: 'TIFF',
    color: '#ECFCCB', textColor: '#3F6212',
    canSign: false, needsConversionToSign: true,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  svg: {
    format: 'SVG', category: 'vector', label: 'SVG',
    color: '#E0F2FE', textColor: '#0369A1',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: true, isPDF: false, isAudio: false,
  },
  txt: {
    format: 'TXT', category: 'text', label: 'TXT',
    color: '#F1F5F9', textColor: '#475569',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  md: {
    format: 'MD', category: 'text', label: 'Markdown',
    color: '#F3E8FF', textColor: '#7E22CE',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  mp3: {
    format: 'MP3', category: 'audio', label: 'MP3',
    color: '#FFEDD5', textColor: '#C2410C',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  m4a: {
    format: 'M4A', category: 'audio', label: 'M4A',
    color: '#FED7AA', textColor: '#9A3412',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  wav: {
    format: 'WAV', category: 'audio', label: 'WAV',
    color: '#FDE68A', textColor: '#92400E',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  ogg: {
    format: 'OGG', category: 'audio', label: 'OGG',
    color: '#FDBA74', textColor: '#C2410C',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  flac: {
    format: 'FLAC', category: 'audio', label: 'FLAC',
    color: '#FEC89A', textColor: '#9A3412',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  aac: {
    format: 'AAC', category: 'audio', label: 'AAC',
    color: '#FFE4C4', textColor: '#EA580C',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  mp4: {
    format: 'MP4', category: 'audio', label: 'MP4',
    color: '#EDE9FE', textColor: '#5B21B6',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  mov: {
    format: 'MOV', category: 'audio', label: 'MOV',
    color: '#E9D5FF', textColor: '#6D28D9',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  webm: {
    format: 'WEBM', category: 'audio', label: 'WEBM',
    color: '#F3E8FF', textColor: '#7C3AED',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: true,
  },
  zip: {
    format: 'ZIP', category: 'unknown', label: 'ZIP',
    color: '#E7E5E4', textColor: '#44403C',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  json: {
    format: 'JSON', category: 'text', label: 'JSON',
    color: '#ECFEFF', textColor: '#0E7490',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  html: {
    format: 'HTML', category: 'text', label: 'HTML',
    color: '#EEF2FF', textColor: '#4338CA',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
  xml: {
    format: 'XML', category: 'text', label: 'XML',
    color: '#F5F5F4', textColor: '#57534E',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  },
}

export function getFileFormatInfo(fileName: string): FileFormatInfo {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
  return FORMAT_MAP[ext] ?? {
    format: 'UNKNOWN', category: 'unknown', label: ext ? ext.toUpperCase() : 'Unknown',
    color: '#F4F4F5', textColor: '#52525B',
    canSign: false, needsConversionToSign: false,
    canOCR: false, isImage: false, isPDF: false, isAudio: false,
  }
}

export function getConversionWarning(formatInfo: FileFormatInfo): string | null {
  if (formatInfo.needsConversionToSign) {
    return `${formatInfo.label} files need to be converted to PDF before signing. Convert automatically?`
  }
  if (formatInfo.format === 'SVG') {
    return `SVG files are vector graphics and can't be signed directly. Convert to PDF or PNG first?`
  }
  if (formatInfo.format === 'CSV') {
    return `CSV files are raw data. Convert to a formatted PDF table first?`
  }
  return null
}
